import React, { useState, useEffect, useRef } from 'react';
import ChartVisualization from './ChartVisualization';
import EnhancedPortfolioChart from './EnhancedPortfolioChart';
import TableVisualization from './TableVisualization';

const VisualizationRenderer = ({ visualizations = [], forceDark = false }) => {

  const [loadingStates, setLoadingStates] = useState({});
  const [errors, setErrors] = useState({});
  const [expandedChart, setExpandedChart] = useState(null);
  const fullscreenRef = useRef(null);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setExpandedChart(null);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (!visualizations || visualizations.length === 0) {
    return null;
  }

  const handleVisualizationLoad = (index) => {
    setLoadingStates(prev => ({ ...prev, [index]: false }));
  };

  const handleVisualizationError = (index, error) => {
    setErrors(prev => ({ ...prev, [index]: error }));
    setLoadingStates(prev => ({ ...prev, [index]: false }));
  };

  const handleExpandChart = (index) => {
    setExpandedChart(index);
    // Enter fullscreen mode if supported
    if (fullscreenRef.current && fullscreenRef.current.requestFullscreen) {
      fullscreenRef.current.requestFullscreen();
    } else if (fullscreenRef.current && fullscreenRef.current.webkitRequestFullscreen) {
      fullscreenRef.current.webkitRequestFullscreen();
    } else if (fullscreenRef.current && fullscreenRef.current.msRequestFullscreen) {
      fullscreenRef.current.msRequestFullscreen();
    }
  };

  const handleExitFullscreen = () => {
    setExpandedChart(null);
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  };

  return (
    <div className={`ai-visualization-container${forceDark ? ' viz-force-dark' : ''}`}>
      <div className="visualization-header">
        <div className="visualization-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="currentColor"/>
          </svg>
          <span>Data Visualization</span>
        </div>
        {visualizations.length > 1 && (
          <span className="visualization-count">{visualizations.length} charts</span>
        )}
      </div>

      <div className="visualization-content">
        {visualizations.map((viz, index) => (
          <div key={index} className="enhanced-visualization-wrapper">
            <div className="visualization-card">
              {loadingStates[index] && (
                <div className="visualization-loading">
                  <div className="loading-spinner"></div>
                  <span>Loading visualization...</span>
                </div>
              )}
              
              {errors[index] ? (
                <div className="visualization-error">
                  <div className="error-icon">⚠️</div>
                  <div>
                    <p>Unable to load visualization</p>
                    <small>{errors[index]}</small>
                  </div>
                </div>
              ) : (
                <>
                  {viz.type === 'chart' ? (
                    <ChartVisualization
                      data={viz.data}
                      forceDark={forceDark}
                      onLoad={() => handleVisualizationLoad(index)}
                      onError={(error) => handleVisualizationError(index, error)}
                    />
                  ) : viz.type === 'enhanced_portfolio_chart' ? (
                    <EnhancedPortfolioChart
                      data={viz.data}
                      onLoad={() => handleVisualizationLoad(index)}
                      onError={(error) => handleVisualizationError(index, error)}
                      showControls={true}
                      enableZoom={true}
                      enableCrosshair={true}
                    />
                  ) : viz.type === 'table' ? (
                    <TableVisualization 
                      data={viz.data}
                      onLoad={() => handleVisualizationLoad(index)}
                      onError={(error) => handleVisualizationError(index, error)}
                    />
                  ) : (
                    <div className="unsupported-visualization">
                      <div className="unsupported-icon">🔧</div>
                      <div>
                        <p>Unsupported visualization type</p>
                        <small>"{viz.type}" is not yet supported</small>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Data source attribution */}
            {viz.data?.metadata?.source && (
              <div className="visualization-attribution">
                <small>Data provided by {viz.data.metadata.source}</small>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Fullscreen Modal */}
      {expandedChart !== null && (
        <div className="chart-fullscreen-modal" ref={fullscreenRef}>
          <div className="fullscreen-header">
            <h2>Expanded View</h2>
            <button 
              className="close-fullscreen-btn"
              onClick={handleExitFullscreen}
              title="Exit fullscreen"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" fill="currentColor"/>
              </svg>
              <span>Exit Fullscreen</span>
            </button>
          </div>
          <div className="fullscreen-content">
            {visualizations[expandedChart] && (
              <div className="expanded-chart-wrapper">
                {visualizations[expandedChart].type === 'chart' ? (
                  <ChartVisualization 
                    data={visualizations[expandedChart].data}
                    isExpanded={true}
                  />
                ) : visualizations[expandedChart].type === 'table' ? (
                  <TableVisualization 
                    data={visualizations[expandedChart].data}
                    isExpanded={true}
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualizationRenderer;